import React, { useState, useEffect, useRef } from "react";
import { Shield, TrendingUp, AlertTriangle, Clock, Target, Zap, Sparkles, ArrowRight, Award, BarChart3, Lock, CheckCircle, Bug, FileText, Terminal, Star, Users, Globe, Rocket, Code, Cloud, MessageSquare } from "lucide-react";

const AnimatedCounter = ({
  end,
  duration = 2000,
  prefix = "",
  suffix = "",
  decimals = 0
}: {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
}) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [inView, setInView] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !inView) {
          setInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [inView]);
  
  useEffect(() => {
    if (!inView) return;
    let startTime: number;
    let animationFrame: number;
    
    const startAnimation = (timestamp: number) => {
      startTime = timestamp;
      animate(timestamp);
    };
    
    const animate = (timestamp: number) => {
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = easeOutQuart * end;
      setCount(currentCount);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(startAnimation);
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration, inView]);
  
  return (
    <span ref={countRef} className="stats-number">
      {prefix}{count.toFixed(decimals)}{suffix}
    </span>
  );
};

const WhyCipherSecurity = () => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const navigateToContact = () => {
    window.location.href = '/contactus';
  };

  // Stats with consistent colors from Hero
  const stats = [
    {
      icon: AlertTriangle,
      value: 4.45,
      prefix: "$",
      suffix: "M",
      decimals: 2,
      label: "Average cost of a data breach in 2024",
      color: "#fbbf24", // Yellow from Offensive Security
      description: "highlighting the critical need for proactive security measures"
    },
    {
      icon: TrendingUp,
      value: 75,
      suffix: "%",
      label: "Of organizations experience security incidents",
      color: "#06b6d4", // Cyan from DevSecOps
      description: "due to inadequate application security practices and testing"
    },
    {
      icon: Clock,
      value: 287,
      suffix: " Days",
      label: "Average time to identify and contain a breach",
      color: "#00a86b", // Green from Application Security
      description: "emphasizing the need for rapid response capabilities"
    }
  ];

  // Value propositions - high level benefits without duplicating detailed services
  const valueProps = [
    {
      icon: CheckCircle,
      title: "Proven Track Record",
      color: "#00a86b",
      description: "Over 500+ successful security assessments across Fortune 500 enterprises, with measurable risk reduction and compliance achievements.",
      stat: "500+ Assessments"
    },
    {
      icon: Zap,
      title: "Rapid Implementation",
      color: "#06b6d4",
      description: "Our streamlined methodologies and automation-first approach deliver security improvements in weeks, not months.",
      stat: "3x Faster Results"
    },
    {
      icon: Award,
      title: "Industry Recognition",
      color: "#fbbf24",
      description: "Certified experts with CISSP, OSCP, and AWS certifications, recognized as thought leaders in cybersecurity.",
      stat: "15+ Certifications"
    },
    {
      icon: BarChart3,
      title: "Measurable ROI",
      color: "#3b82f6",
      description: "Average 4:1 return on security investment through reduced incident costs and improved operational efficiency.",
      stat: "4:1 ROI Average"
    },
    {
      icon: Shield,
      title: "Comprehensive Defense",
      color: "#a855f7",
      description: "Multi-layered security architecture combining prevention, detection, and response capabilities to protect against evolving threats.",
      stat: "360° Protection"
    },
    {
      icon: Target,
      title: "Risk Reduction Focus",
      color: "#ef4444",
      description: "Proactive threat identification and mitigation strategies that reduce your organization's overall security risk by an average of 90%.",
      stat: "90% Risk Reduction"
    }
  ];


  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            33% {
              transform: translateY(-10px) rotate(-1deg);
            }
            66% {
              transform: translateY(-5px) rotate(1deg);
            }
          }

          .animate-fade-in {
            animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }

          .animate-scale-in {
            animation: scaleIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
          }

          .stats-number {
            font-variant-numeric: tabular-nums;
            font-weight: 700;
          }

          .gradient-text {
            background: linear-gradient(135deg, #00a86b 0%, #06b6d4 50%, #00a86b 100%);
            background-size: 200% 200%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .service-card {
            transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
            transform-style: preserve-3d;
          }

          .service-card:hover {
            transform: translateY(-8px);
          }

          .stat-card {
            transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          }

          .hover-glow {
            position: relative;
            overflow: hidden;
          }

          .hover-glow::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
            transition: left 0.6s;
          }

          .hover-glow:hover::before {
            left: 100%;
          }
        `}
      </style>

      <section id="why-cipher" className="relative pt-24 pb-8 overflow-hidden bg-[#0f0f17]">
        {/* Background effects matching Hero */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f0f17] via-[#0a0a12] to-[#0f0f17]" />
          
          {/* Orbs matching Hero style */}
          <div 
            className="absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle at 30% 30%, rgba(0, 168, 107, 0.12) 0%, rgba(6, 182, 212, 0.08) 50%, transparent 70%)',
              filter: 'blur(60px)',
              animation: isLoaded ? 'pulse 8s ease-in-out infinite' : 'none'
            }}
          />
          <div 
            className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] rounded-full opacity-[0.15]"
            style={{
              background: 'radial-gradient(circle at 70% 70%, rgba(251, 191, 36, 0.10) 0%, rgba(168, 85, 247, 0.06) 50%, transparent 80%)',
              filter: 'blur(80px)',
              animation: isLoaded ? 'pulse 10s ease-in-out infinite 1s' : 'none'
            }}
          />
          
          {/* Subtle grid overlay */}
          <div 
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(0, 168, 107, 0.4) 1px, transparent 1px),
                radial-gradient(circle at 75% 75%, rgba(6, 182, 212, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '120px 120px, 80px 80px'
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Value Propositions Section */}
          <div className="mb-40">
            <div className="text-center mb-16">
              <h3 className="text-5xl md:text-7xl font-bold mb-6">
                <span className="text-white">Why Choose</span>
                <br />
                <span className="gradient-text">CIPHER Security</span>
              </h3>
              <p className="text-xl text-gray-300 max-w-4xl mx-auto">
                Our proven methodologies and expert team deliver measurable security improvements that protect your assets and enables the business.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {valueProps.map((prop, index) => {
                const Icon = prop.icon;
                const isHovered = hoveredCard === index + 10;
                
                return (
                  <div
                    key={index}
                    className={`service-card group ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    onMouseEnter={() => setHoveredCard(index + 10)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    <div
                      className="h-full p-8 rounded-2xl border backdrop-blur-sm"
                      style={{
                        borderColor: isHovered ? prop.color : `${prop.color}30`,
                        backgroundColor: isHovered ? `${prop.color}15` : `${prop.color}08`,
                        boxShadow: isHovered ? `0 20px 40px ${prop.color}20` : 'none'
                      }}
                    >
                      <div
                        className={`inline-flex p-3 rounded-xl mb-6 ${isHovered ? 'animate-float' : ''}`}
                        style={{
                          backgroundColor: `${prop.color}20`,
                          color: prop.color,
                          boxShadow: `0 4px 16px ${prop.color}20`
                        }}
                      >
                        <Icon className={`w-8 h-8 transition-transform duration-300 ${
                          isHovered ? 'scale-110' : ''
                        }`} />
                      </div>
                      
                      <h4 className="text-2xl font-bold text-white mb-4">
                        {prop.title}
                      </h4>
                      
                      <p className="text-gray-400 mb-6">
                        {prop.description}
                      </p>
                      
                      <div
                        className="flex items-center gap-2 text-sm font-semibold"
                        style={{ color: prop.color }}
                      >
                        <Zap className="w-4 h-4" />
                        <span>{prop.stat}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Security That Drives Growth Section - moved after Why Choose */}
          <div className={`text-center mb-20 ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}>
            <h2 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="text-white">Cybersecurity as</span>
              <br />
              <span className="gradient-text">the Enabler</span>
            </h2>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              In a landscape where security breaches cost millions and compliance failures destroy reputations,
              we deliver comprehensive protection with{" "}
              <span className="text-white font-semibold">proven expertise</span> and{" "}
              <span className="text-white font-semibold">measurable results</span>.
            </p>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              const isHovered = hoveredCard === index;
              
              return (
                <div
                  key={index}
                  className={`stat-card text-center p-10 rounded-2xl border backdrop-blur-sm ${
                    isLoaded ? 'animate-scale-in' : 'opacity-0'
                  }`}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    borderColor: isHovered ? stat.color : `${stat.color}30`,
                    backgroundColor: `${stat.color}08`,
                    boxShadow: isHovered ? `0 20px 40px ${stat.color}20` : 'none'
                  }}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div 
                    className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                      isHovered ? 'animate-float' : ''
                    }`}
                    style={{
                      backgroundColor: `${stat.color}20`,
                      boxShadow: `0 8px 24px ${stat.color}30`
                    }}
                  >
                    <Icon className="w-10 h-10" style={{ color: stat.color }} />
                  </div>
                  
                  <h3 className="text-4xl font-bold text-white mb-2">
                    <AnimatedCounter 
                      end={stat.value} 
                      decimals={stat.decimals || 0}
                      prefix={stat.prefix}
                      suffix={stat.suffix}
                    />
                  </h3>
                  
                  <div 
                    className="w-16 h-1 mx-auto mb-4 rounded-full"
                    style={{ backgroundColor: stat.color }}
                  />
                  
                  <p className="text-lg text-gray-300">
                    {stat.label}
                    <span className="block text-gray-400 mt-2">{stat.description}</span>
                  </p>
                </div>
              );
            })}
          </div>

        </div>
      </section>
    </>
  );
};

export default WhyCipherSecurity;